<template>
  <div id="markdown-content" v-if="displayAsPlainText">{{ content }}</div>
  <div id="markdown-content" v-else v-html="content"></div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import MarkdownItMark from 'markdown-it-mark';
import MarkdownItModifyToken from 'markdown-it-modify-token';
import MarkdownItHeaderSections from 'markdown-it-header-sections';
import MarkdownItPlainText from 'markdown-it-plain-text';
import { computed, defineComponent, ref, onUpdated, onBeforeUnmount, onMounted } from 'vue';

import { openGallery, default as galleryModalComponent } from '@/components/Gallery.vue';

import { useStore } from 'vuex';

import { TRANSPARENT_PNG } from '@/utils/media';

import _ from 'lodash';

//USE WITH CARE, BECAUSE OF UNFILTERED HTML!
export default defineComponent({
  name: 'MarkdownContent',
  props: {
    source: {
      type: String
    },
    displayAsPlainText: {
      type: Boolean,
      default: false
    },
    showAbbreviationIncompleteSentence: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      required: false,
      default: () => {}
    },
    plugins: { //Allow giving names of plugins to use
      type: Array,
      required: false,
      default: () => []
    },
  },
  setup(props) {
    const store = useStore();

    const objectURLs = ref({});

    const loadedThroughRelay = computed(() => store.getters['auth/isAuthenticationRelayActive']);

    const mediaItems = ref({});

    const generateMediaID = function(mediaIndex) {
      return `media-item-${mediaIndex}`;
    }

    const openGalleryModal = function(event, mediaIndex) {
      if (mediaItems.value != null && mediaIndex in mediaItems.value) {
        let sortedMediaItems = _.sortBy(Object.values(mediaItems.value), ['mediaIndex']);
        let offset;
        let elementHeight;
        if (event != null && event.target != null){
          let elementBounds = event.target.getBoundingClientRect();

          offset = {
            x: (elementBounds.left + elementBounds.right) / 2,
            y: (elementBounds.top + elementBounds.bottom) / 2
          };

          elementHeight = elementBounds.height;
        }

        openGallery(galleryModalComponent, sortedMediaItems, mediaIndex, {
          closeCallback: (lastIndex) => {
            if (lastIndex != mediaIndex) {
              //On close of the gallery, scroll to the last viewed item if the index changed
              let mediaElement = document.getElementById(generateMediaID(lastIndex));
              if (mediaElement != null) {
                mediaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }
        }, offset, elementHeight);
      }
    }

    const addMediaItem = function(mediaItem, mediaIndex, url, mime) {
      mediaItem.setAttribute('src', url);
      mediaItem.setAttribute('id', generateMediaID(mediaIndex));
      mediaItem.onclick = (event) => openGalleryModal(event, mediaIndex);
      mediaItem.classList.add('gallery-image');
      mediaItems.value[mediaIndex] = {
        blobURL: url,
        mime: mime || 'image/png',
        mediaIndex
      };
    }

    const updateView = function() {
      let items = document.querySelectorAll('#markdown-content video source, #markdown-content img, #markdown-content audio');
      items.forEach((mediaItem, mediaItemIndex) => {
        if (mediaItem != null) {
          let currentURL = mediaItem.getAttribute('data-src');
          mediaItem.removeAttribute('data-src'); //Remove immediately in case it gets called multiple times

          let currentMime = mediaItem.getAttribute('data-mime');

          if (currentURL != null) {
            if (loadedThroughRelay.value) {
              addMediaItem(mediaItem, mediaItemIndex, currentURL, currentMime);
            } else { //Load it manually - no relay available 
              store.dispatch('fetchMediaWithAuthentication', {mediaURL: currentURL})
              .then(({ url, needsRevoking } = {}) => {
                if (url != null) {
                  if (needsRevoking) objectURLs.value[currentURL] = url; //Set in objectURLs only if it needs to be revoked
                  addMediaItem(mediaItem, mediaItemIndex, url, currentMime);
                }
              })
              .catch((error) => {
                console.error('Error loading media with authentication', error)
                mediaItem.setAttribute('src', currentURL);
              });
            }
          }
        }
      });
    }

    //This gets triggered when content is loaded after view become visible
    onUpdated(() => {
      updateView();
    });

    //This gets triggered immediately after load - Both cases need to be checked as content can be either ready already or not
    onMounted(() => {
      updateView();
    });

    onBeforeUnmount(() => {
      _.forEach(objectURLs.value, (objectURL) => {
        URL.revokeObjectURL(objectURL.value);
      });
      objectURLs.value = {};
    });

    const md = ref(new MarkdownIt({...props.options,
      html: false,
      modifyToken: function (token, env) {
        //First apply internal transformations
        //If a modify function is included in the options, call it too
        if (props.options != null && props.options.modifyToken != null) {
          props.options.modifyToken(token, env);
        }

        //Set sources as another parameter to be correctly loaded depending on the platform
        if (token.type === 'image' || token.type === 'video') {
          if (token.attrObj.src !== null) {
            let currentSrc = token.attrObj.src;
            token.attrObj['data-src'] = currentSrc;
            token.attrObj.src = TRANSPARENT_PNG; //Set image to initially empty image to avoid network errors
            token.attrObj.draggable = false;

            //Try to parse the URL to get metadata and set it on the object
            try { //TODO Check if parsing and then rendering different elements based on that can be trusted!
              let parsedURL = new URL(currentSrc);
              if (parsedURL != null && parsedURL.searchParams != null) {
                if (parsedURL.searchParams.has('mime')) token.attrObj['data-mime'] = parsedURL.searchParams.get('mime');
              }
              //TODO If mime includes video, show thumbnail!
              //TODO Maybe add support for PDF too!
            } catch (error) {
              console.error("Could not parse provided media URL in MarkdownContent", error);
            }
          }
        }
      }
    }));
    md.value.use(MarkdownItMark);
    md.value.use(MarkdownItModifyToken);
    md.value.use(MarkdownItPlainText);
    md.value.use(MarkdownItHeaderSections);

    for (const pluginName of _.uniq(props.plugins)) {
      switch (pluginName) {

        default:
          break;
      }
    }

    const content = computed(() => {
      if (props.source != null) {
        try {
          let htmlSource = md.value.render(props.source);

          //Return the plain text from the plugin, if it is requested
          if (props.displayAsPlainText) {
            if (md.value.plainText == null || md.value.plainText.length <= 0) return null;
            let processedText = md.value.plainText.trim();
            if (props.showAbbreviationIncompleteSentence && (!(_.endsWith(processedText, '.')))) processedText += '\u2026'; //Ellipsis on incomplete sentences
            return processedText;
          }

          return htmlSource;
        } catch {
          console.error('Could not parse markdown source!');
        }
      }
      return null;
    });

    return { content };
  },
});
</script>

<style>
  #markdown-content p, #markdown-content li {
    font-weight: var(--weight-level-light);
  }

  #markdown-content section > section {
    margin-left: 2ch;
  }

  #markdown-content h1, #markdown-content h2 {
    font-weight: var(--weight-level-semibold);
    margin-top: 2em;
  }

  #markdown-content h3, #markdown-content h4 {
    font-weight: var(--weight-level-default);
  }

  #markdown-content img {
    border-radius: var(--global-border-radius);
    max-height: 400px;
  }

  #markdown-content hr {
    margin-block: 3em;
    color: var(--ion-color-medium-light);
    background: var(--ion-color-medium-light);
  }

  #markdown-content .gallery-image {
    cursor: pointer;
  }
</style>